// Batch 0 (UX/IA overhaul) — id → name resolution.
//
// Kills the ad-hoc `entity_id.slice(0,8)…` / `cam:1a2b…` truncated-UUID
// identity scattered across Alerts, Detections, Evidence, Case detail,
// Entities, and UserDetail. A page builds a resolver from whatever list it
// already fetched (cameras, sites, entities, users) and resolves ids to names,
// with a stable short-id fallback ONLY when nothing matches.
//
// Pure + data-source-agnostic on purpose: the consuming batch passes
// `useCameras().data?.items` / `useSites().data` / etc., so this module never
// imports the API layer and stays trivially testable.
import { shortId } from "@/types/deviceRef";

export type NameResolver = (
  id: string | null | undefined,
  fallback?: string,
) => string;

interface ResolveOpts<T> {
  idKey?: keyof T;
  nameKey?: keyof T;
}

/**
 * Build a resolver from a list of records. Unknown / missing ids return the
 * explicit `fallback` if given, else `shortId(id)` (never a raw full UUID,
 * never blank).
 */
export function makeNameResolver<T extends object>(
  items: T[] | undefined | null,
  opts: ResolveOpts<T> = {},
): NameResolver {
  const idKey = (opts.idKey ?? "id") as keyof T;
  const nameKey = (opts.nameKey ?? "name") as keyof T;
  const index = new Map<string, string>();
  for (const item of items ?? []) {
    const id = item?.[idKey] as unknown;
    const name = item?.[nameKey] as unknown;
    if (typeof id === "string" && typeof name === "string" && name.trim()) {
      index.set(id, name);
    }
  }
  return (id, fallback) => {
    if (!id) return fallback ?? "—";
    return index.get(id) ?? fallback ?? shortId(id);
  };
}
